import { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, XCircle, Clock } from 'lucide-react';
import { RichTextDisplay } from '../common/RichTextEditor';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface BlastDetailProps {
  blastId: string;
  onBack: () => void;
}

interface Blast {
  _id: string;
  subject: string;
  body: string;
  audienceType: string;
  status: string;
  sentAt?: string;
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  createdAt: string;
  createdBy?: { profile: { firstName: string; lastName: string }; email: string };
  recipients: {
    email: string;
    userId: string;
    role: string;
    status: string;
    error?: string;
  }[];
}

const audienceLabels: Record<string, string> = {
  all_ams: 'All Account Managers',
  all_physicians: 'All Physicians',
  all: 'All Users',
  pods: 'Specific Pods'
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'sent':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Sent</span>;
    case 'failed':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="h-3 w-3" /> Failed</span>;
    case 'sending':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3" /> Sending</span>;
    case 'draft':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Draft</span>;
    case 'pending':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"><Clock className="h-3 w-3" /> Pending</span>;
    default:
      return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{status}</span>;
  }
};

export function BlastDetail({ blastId, onBack }: BlastDetailProps) {
  const [blast, setBlast] = useState<Blast | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlast();
  }, [blastId]);

  const fetchBlast = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<{ success: boolean; data: Blast }>(`/blasts/${blastId}`);
      if (response.success) {
        setBlast(response.data);
      }
    } catch (error) {
      toast.error('Failed to load blast details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!blast) {
    return (
      <div className="text-center py-12" style={{ color: 'var(--text-tertiary)' }}>
        Blast not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            {blast.subject}
          </h3>
          <div className="flex items-center gap-3 mt-1">
            {statusBadge(blast.status)}
            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              {audienceLabels[blast.audienceType] || blast.audienceType}
            </span>
            {blast.sentAt && (
              <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                Sent {new Date(blast.sentAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      {blast.status !== 'draft' && (
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border p-4" style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)' }}>
            <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{blast.totalRecipients}</div>
            <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Total Recipients</div>
          </div>
          <div className="rounded-lg border p-4" style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)' }}>
            <div className="text-2xl font-bold text-green-600">{blast.successCount}</div>
            <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Delivered</div>
          </div>
          <div className="rounded-lg border p-4" style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)' }}>
            <div className="text-2xl font-bold text-red-600">{blast.failureCount}</div>
            <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Failed</div>
          </div>
        </div>
      )}

      {/* Body Preview */}
      <div className="rounded-lg border p-4" style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)' }}>
        <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>Email Content</h4>
        <RichTextDisplay content={blast.body} />
      </div>

      {/* Recipient Table */}
      {blast.recipients && blast.recipients.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
            Recipients ({blast.recipients.length})
          </h4>
          <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--border-primary)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--surface-secondary)' }}>
                  <th className="text-left px-4 py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Email</th>
                  <th className="text-left px-4 py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Role</th>
                  <th className="text-left px-4 py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Status</th>
                  <th className="text-left px-4 py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {blast.recipients.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: 'var(--border-primary)' }}>
                    <td className="px-4 py-2" style={{ color: 'var(--text-primary)' }}>{r.email}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100" style={{ color: 'var(--text-tertiary)' }}>
                        {r.role === 'distributor' ? 'AM' : 'Physician'}
                      </span>
                    </td>
                    <td className="px-4 py-2">{statusBadge(r.status)}</td>
                    <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>{r.error || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
